// What's next for the student, as cards under Anna's greeting (N4, HomeCards.js) and as the
// closed-chat launcher's hint when there is no test to take or continue.
//
// One card per kind, in this order of usefulness:
//   result   a released result they haven't opened yet (my-results + the Results page's seen list)
//   session  their next session, with the counselor's photo
//   due      a test due within a week (useMyTests, GET /api/my-tests/)
//   programs the programs they saved from the career test
// No messaging card while student messages are switched off.
import api from '../../utils/api';
import { readSeenResults, resultKey } from '../pages/studentResults/resultStates';
import { ACTIVE, endOf, startOf, whenLabel } from '../pages/studentBooking/studentBooking';
import { dueDate, testName } from './useMyTests';

const DUE_SOON_DAYS = 7;
// A result counts as new on a device that never opened Results only if it's this recent
const FIRST_VISIT_NEW_DAYS = 30;
const FRESH_MS = 30 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const tx = (lang, en, fil) => (lang === 'fil' ? fil : en);
const QUIET = { skipErrorToast: true };

let inflight = null;
let last = null;

/** Whether this student has the career test, and so saved programs (Programs in the header). */
export const showsPrograms = (user) =>
  user?.shows_programs !== false && (!Array.isArray(user?.assigned_assessments) || user.assigned_assessments.includes('riasec'));

/** Tests only: forget the shared reply. */
export const resetHomeData = () => {
  inflight = null;
  last = null;
};

/**
 * { results, appointments, saved } for the signed-in student. The chat and the launcher both ask
 * when they open, so a reply this fresh is shared instead of asking the one server again.
 */
export const loadHomeData = async ({ programs = true, fresh = false } = {}) => {
  if (!fresh && last && Date.now() - last.at < FRESH_MS && (!programs || last.data.saved)) return last.data;
  if (!inflight) {
    const value = (result, pick) => (result.status === 'fulfilled' && result.value ? pick(result.value.data) : null);
    inflight = Promise.allSettled([
      api.get('/api/booking/my-results/', QUIET),
      api.get('/api/booking/appointments/', QUIET),
      programs ? api.get('/api/saved-programs/', QUIET) : Promise.resolve(null),
    ])
      .then(([results, appointments, saved]) => {
        const data = {
          results: value(results, (d) => (Array.isArray(d?.results) ? d.results : [])) || [],
          appointments: value(appointments, (d) => (Array.isArray(d?.appointments) ? d.appointments : [])) || [],
          saved: programs ? value(saved, (d) => (Array.isArray(d?.programs) ? d.programs : [])) || [] : null,
        };
        last = { at: Date.now(), data };
        return data;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

const daysUntil = (date, now) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((date.getTime() - today.getTime()) / DAY_MS);
};

const dueKicker = (due, now, lang) => {
  const date = dueDate(due);
  const days = daysUntil(date, now);
  if (days === 0) return tx(lang, 'Due today', 'Hanggang ngayong araw');
  if (days === 1) return tx(lang, 'Due tomorrow', 'Hanggang bukas');
  const label = date.toLocaleDateString(lang === 'fil' ? 'fil-PH' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  return tx(lang, `Due ${label}`, `Hanggang ${label}`);
};

const newResult = (results, seen, now) =>
  (results || []).find((row) => {
    if (seen) return !seen.has(resultKey(row.type, row.id));
    const finished = new Date(row.completed_at);
    return !Number.isNaN(finished.getTime()) && now - finished < FIRST_VISIT_NEW_DAYS * DAY_MS;
  }) || null;

/**
 * The cards to show, most useful first. `tests` is the useMyTests list (or null while loading),
 * `userId` keys the Results page's seen list, `programs` says whether this student has the
 * career test (and so saved programs).
 */
export const buildHomeCards = ({ data, tests = null, userId = null, programs = true, lang = 'en', now = new Date() }) => {
  const cards = [];
  if (!data) return cards;

  const result = newResult(data.results, userId ? readSeenResults(userId) : null, now);
  if (result) {
    cards.push({
      key: 'result',
      type: result.type,
      kicker: tx(lang, 'Result ready', 'Handa na ang resulta'),
      title: testName(result.type, lang),
      button: { label: tx(lang, 'See my result', 'Tingnan ang resulta ko'), go: 'results', test: result.type },
    });
  }

  const next = (data.appointments || [])
    .filter((row) => ACTIVE.includes(row.status) && endOf(row) > now)
    .sort((a, b) => startOf(a) - startOf(b))[0];
  if (next) {
    const counselor = next.counselor || {};
    cards.push({
      key: 'session',
      kicker: tx(lang, 'Your next session', 'Ang susunod mong session'),
      title: whenLabel(next.date, next.time_slot),
      sub: counselor.name ? tx(lang, `with ${counselor.name}`, `kasama si ${counselor.name}`) : '',
      counselor: { name: counselor.name || '', photoUrl: counselor.photo_url || null },
      button: { label: tx(lang, 'See my session', 'Tingnan ang session ko'), go: 'sessions' },
    });
  }

  const due = (tests || [])
    .filter((row) => (row.status === 'open' || row.status === 'in_progress') && row.due && row.reason !== 'not_assigned')
    .map((row) => ({ row, days: daysUntil(dueDate(row.due), now) }))
    .filter(({ days }) => days >= 0 && days <= DUE_SOON_DAYS)
    .sort((a, b) => a.days - b.days)[0];
  if (due) {
    const started = due.row.status === 'in_progress' && Number(due.row.answered) > 0;
    cards.push({
      key: 'due',
      type: due.row.type,
      kicker: dueKicker(due.row.due, now, lang),
      title: testName(due.row.type, lang),
      sub: started && due.row.total
        ? tx(lang, `${due.row.answered} of ${due.row.total} answered`, `${due.row.answered} sa ${due.row.total} ang nasagot`)
        : '',
      button: {
        label: started ? tx(lang, 'Continue the test', 'Ituloy ang test') : tx(lang, 'Start the test', 'Simulan ang test'),
        go: 'test',
        test: due.row.type,
      },
    });
  }

  const saved = programs ? data.saved || [] : [];
  if (saved.length) {
    const names = saved.map((row) => row.course_name).filter(Boolean);
    cards.push({
      key: 'programs',
      kicker: tx(lang, 'Saved programs', 'Mga naka-save na programa'),
      title: names.length === 1 ? names[0] : tx(lang, `${names.length} programs saved`, `${names.length} programang naka-save`),
      sub: names.length > 1 ? names.slice(0, 2).join(' · ') + (names.length > 2 ? ' …' : '') : '',
      button: { label: tx(lang, 'See my programs', 'Tingnan ang mga programa ko'), go: 'programs' },
    });
  }
  return cards;
};

/** The launcher's line for the most useful card: { title, subtitle }. */
export const launcherHint = (card, lang = 'en') => {
  if (!card) return null;
  switch (card.key) {
    case 'result':
      return { title: tx(lang, 'Your result is ready', 'Handa na ang resulta mo'), subtitle: card.title };
    case 'session':
      return { title: tx(lang, 'Your next session', 'Ang susunod mong session'), subtitle: card.title };
    case 'due':
      return { title: card.kicker, subtitle: card.title };
    case 'programs':
      return { title: tx(lang, 'Your saved programs', 'Mga naka-save mong programa'), subtitle: card.title };
    default:
      return null;
  }
};
